// Agent test dialog - modal window for testing agents
import { useState, useEffect } from 'react'
import { Bot, Loader2, AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { executeAgent, type Agent } from '@/modules/agents/agent-client'

interface AgentTestDialogProps {
  agent: Agent | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AgentTestDialog({ agent, open, onOpenChange }: AgentTestDialogProps) {
  const [prompt, setPrompt] = useState('Hello, can you confirm you are working?')
  const [apiKey, setApiKey] = useState('')
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setResult(null)
      setError(null)
      setTesting(false)
    }
  }, [open])

  const handleTest = async () => {
    if (!agent || !prompt.trim()) return

    setTesting(true)
    setResult(null)
    setError(null)

    try {
      const agentId = agent.agent_id || agent.assistant_id
      const response = await executeAgent(agentId, {
        input: prompt,
        api_key: apiKey || undefined,
      })
      setResult(response.response || response.result || JSON.stringify(response, null, 2))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setTesting(false)
    }
  }

  if (!open || !agent) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b p-6">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-kaycoo-teal-dark flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Test Agent: {agent.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {agent.description || 'No description'}
            </p>
            <p className="text-xs text-muted-foreground font-mono mt-1">
              Provider: {agent.provider} | Model: {agent.model_id}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="ml-4"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-6">
          {/* Test Prompt */}
          <div className="space-y-2">
            <Label htmlFor="test-prompt">Test Prompt</Label>
            <Textarea
              id="test-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="Enter a test prompt..."
              className="resize-none"
            />
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="test-api-key">
              API Key ({agent.provider})
              <span className="text-muted-foreground ml-1">(optional if agent has key configured)</span>
            </Label>
            <Input
              id="test-api-key"
              type="password"
              placeholder="sk-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>

          {/* Test Button */}
          <Button
            onClick={handleTest}
            disabled={testing || !prompt.trim()}
            className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Bot className="mr-2 h-4 w-4" />
                Execute Test
              </>
            )}
          </Button>

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-2">
              <Label>Response</Label>
              <div className="max-h-80 overflow-y-auto rounded-lg border bg-gray-50 p-4 text-sm whitespace-pre-wrap">
                {result}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
